import { useUnistyles } from 'react-native-unistyles';
import { tokens } from '../design-tokens';

const baseTheme = {
  colors: {
    paper: tokens.colors.ink.paper,
    paperDark: tokens.colors.ink.paperDark,
    press: tokens.colors.ink.press,
    pressMuted: tokens.colors.ink.pressMuted,
    acid: tokens.colors.ink.acid,
    acidPressed: tokens.colors.ink.acidPressed,
    field: tokens.colors.ink.field,
    fieldDeep: tokens.colors.ink.fieldDeep,
    maroon: tokens.colors.ink.maroon,
    background: tokens.semanticColors.light.background,
    surface: tokens.semanticColors.light.card,
    surfaceForeground: tokens.semanticColors.light.cardForeground,
    text: tokens.semanticColors.light.foreground,
    textSecondary: tokens.semanticColors.light.mutedForeground,
    textInverse: tokens.semanticColors.light.primaryForeground,
    primary: tokens.semanticColors.light.primary,
    primaryForeground: tokens.semanticColors.light.primaryForeground,
    secondary: tokens.semanticColors.light.secondary,
    secondaryForeground: tokens.semanticColors.light.secondaryForeground,
    muted: tokens.semanticColors.light.muted,
    mutedForeground: tokens.semanticColors.light.mutedForeground,
    accent: tokens.semanticColors.light.accent,
    accentForeground: tokens.semanticColors.light.accentForeground,
    destructive: tokens.semanticColors.light.destructive,
    destructiveForeground: tokens.semanticColors.light.destructiveForeground,
    border: tokens.semanticColors.light.border,
    input: tokens.semanticColors.light.input,
    ring: tokens.semanticColors.light.ring,
    success: tokens.colors.state.success,
    successForeground: tokens.colors.text.inverse,
    successBackground: tokens.colors.state.successBg,
    warning: tokens.colors.state.warning,
    warningForeground: tokens.colors.text.primary,
    warningBackground: tokens.colors.state.warningBg,
    info: tokens.colors.state.info,
    infoForeground: tokens.colors.text.inverse,
    infoBackground: tokens.colors.state.infoBg,
    error: tokens.colors.state.error,
    errorForeground: tokens.colors.text.inverse,
    errorBackground: tokens.colors.state.errorBg,
    neutral50: tokens.colors.neutral[50],
    neutral100: tokens.colors.neutral[100],
    neutral200: tokens.colors.neutral[200],
    neutral300: tokens.colors.neutral[300],
    neutral400: tokens.colors.neutral[400],
    neutral500: tokens.colors.neutral[500],
    neutral600: tokens.colors.neutral[600],
    neutral700: tokens.colors.neutral[700],
    neutral800: tokens.colors.neutral[800],
    neutral900: tokens.colors.neutral[900],
    primary50: tokens.colors.primary[50],
    primary100: tokens.colors.primary[100],
    primary500: tokens.colors.primary[500],
    primary600: tokens.colors.primary[600],
    primary700: tokens.colors.primary[700],
    primary800: tokens.colors.primary[800],
    success600: tokens.colors.success[600],
    warning800: tokens.colors.warning[800],
    error400: tokens.colors.error[400],
    error500: tokens.colors.error[500],
    error700: tokens.colors.error[700],
    error800: tokens.colors.error[800],
    error900: tokens.colors.error[900],
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
  },
  spacing: {
    none: 0,
    xs: tokens.spacing.xs,
    s: tokens.spacing.sm,
    m: tokens.spacing.md,
    base: tokens.spacing.base,
    l: tokens.spacing.lg,
    xl: tokens.spacing.xl,
    xxl: tokens.spacing['2xl'],
    '0': 0,
    '1': tokens.spacing.xs,
    '2': tokens.spacing.sm,
    '3': tokens.spacing.md,
    '4': tokens.spacing.base,
    '5': tokens.spacing.lg,
    '6': tokens.spacing.xl,
    '8': tokens.spacing['2xl'],
  },
  borderRadii: {
    none: tokens.borderRadius.none,
    s: tokens.borderRadius.sm,
    m: tokens.borderRadius.md,
    l: tokens.borderRadius.lg,
    xl: tokens.borderRadius.xl,
    xxl: tokens.borderRadius['2xl'],
    full: tokens.borderRadius.full,
  },
  breakpoints: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
  },
  typography: {
    fontFamily: tokens.typography.fontFamily,
  },
  shadows: {
    blockSm: tokens.shadows.sm,
    block: tokens.shadows.base,
    blockLg: tokens.shadows.lg,
  },
  textVariants: {
    defaults: {
      fontSize: tokens.typography.fontSize.md,
      lineHeight: 24,
      fontFamily: tokens.typography.fontFamily.body,
      color: tokens.colors.text.primary,
    },
    h1: {
      fontSize: tokens.typography.fontSize['4xl'],
      lineHeight: 46,
      fontFamily: tokens.typography.fontFamily.display,
      fontWeight: '900',
      letterSpacing: -0.9,
      textTransform: 'uppercase',
      color: tokens.colors.text.primary,
    },
    h2: {
      fontSize: tokens.typography.fontSize['3xl'],
      lineHeight: 38,
      fontFamily: tokens.typography.fontFamily.display,
      fontWeight: '900',
      letterSpacing: -0.7,
      textTransform: 'uppercase',
      color: tokens.colors.text.primary,
    },
    h3: {
      fontSize: tokens.typography.fontSize['2xl'],
      lineHeight: 30,
      fontFamily: tokens.typography.fontFamily.display,
      fontWeight: '900',
      letterSpacing: -0.5,
      textTransform: 'uppercase',
      color: tokens.colors.text.primary,
    },
    title: {
      fontSize: tokens.typography.fontSize.xl,
      lineHeight: 26,
      fontFamily: tokens.typography.fontFamily.bodyMedium,
      fontWeight: '700',
      color: tokens.colors.text.primary,
    },
    body: {
      fontSize: tokens.typography.fontSize.md,
      lineHeight: 24,
      fontFamily: tokens.typography.fontFamily.body,
      color: tokens.colors.text.primary,
    },
    bodySmall: {
      fontSize: tokens.typography.fontSize.sm,
      lineHeight: 18,
      fontFamily: tokens.typography.fontFamily.body,
      color: tokens.colors.text.primary,
    },
    caption: {
      fontSize: tokens.typography.fontSize.xs,
      lineHeight: 14,
      fontFamily: tokens.typography.fontFamily.mono,
      fontWeight: '600',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: tokens.colors.text.secondary,
    },
    button: {
      fontSize: tokens.typography.fontSize.md,
      lineHeight: 20,
      fontFamily: tokens.typography.fontFamily.mono,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: tokens.colors.text.primary,
    },
  },
} as const;

export const lightTheme = baseTheme;

export const appThemes = {
  light: lightTheme,
} as const;

export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
} as const;

export type Theme = typeof lightTheme;
export type ThemeName = keyof typeof appThemes;

export const theme = lightTheme;

export function useTheme<T = Theme>(): T {
  const { theme: activeTheme } = useUnistyles();
  return activeTheme as T;
}

export function useAppTheme(): Theme {
  return useTheme<Theme>();
}
